/**
 * MSW 기반 500 에러 핸들링 테스트 유틸
 *
 * ✅ 이전 방식 (Axios 인터셉터 가짜 에러) 대비 개선점:
 *   - Service Worker 레벨에서 실제 HTTP 500 응답 반환
 *   - error.request (XMLHttpRequest) 가 정상 존재
 *   - 브라우저 Network 탭에서 500 응답 직접 확인 가능
 *   - Axios 전체 파이프라인 (retry → onResError) 이 실제와 동일하게 동작
 *
 * 사용법: 브라우저 콘솔에서
 *   __errorTest.single500()     → 단일 500 에러 (모달 피드백만)
 *   __errorTest.multiple500()   → 연속 500 에러 (공사중 페이지 이동)
 *   __errorTest.getRetry()      → GET 재시도 확인 (Network 탭에서 3번 요청 확인!)
 *   __errorTest.postNoRetry()   → POST는 재시도 안 하는지 확인
 *   __errorTest.enable500()     → 이후 모든 요청을 500으로 변환 (수동 테스트용)
 *   __errorTest.disable500()    → 500 변환 해제
 */

import instance from './instance';
import { __resetServerErrorCount } from './onResponse';
import type { SetupWorker } from 'msw/browser';
import {
  createAll500Handler,
  createCountedAll500Handler,
} from '../../../mocks/handlers';

const TEST_PREFIX = '[ErrorTest:MSW]';
const REAL_API = '/v1/public/banners';

let workerRef: SetupWorker | null = null;

/** MSW worker 참조 설정 (initMsw에서 호출) */
export const setWorkerRef = (worker: SetupWorker) => {
  workerRef = worker;
};

const getWorker = (): SetupWorker => {
  if (!workerRef) {
    throw new Error(
      `${TEST_PREFIX} MSW worker가 아직 초기화되지 않았습니다. 페이지 로드 후 다시 시도하세요.`
    );
  }
  return workerRef;
};

export const registerErrorTest = () => {
  if (process.env.NODE_ENV === 'production') return;

  let is500Enabled = false;

  const errorTest = {
    /** 테스트 1: 단일 500 에러 → 모달 피드백만 나와야 함 */
    async single500() {
      __resetServerErrorCount(); // 이전 테스트 카운터 누적 방지
      console.log(`${TEST_PREFIX} 단일 500 에러 테스트 시작`);
      console.log(
        `${TEST_PREFIX} 예상: 모달 "서버가 일시적으로 불안정합니다" 표시`
      );
      console.log(`${TEST_PREFIX} Network 탭에서 500 응답을 직접 확인하세요!`);

      const worker = getWorker();
      // GET은 retry 2회 = 총 3번 요청. 단일 에러 테스트이므로 3번만 500
      const handler = createCountedAll500Handler(3);
      worker.use(handler);

      try {
        await instance.get(REAL_API);
      } catch (e: any) {
        console.log(`${TEST_PREFIX} ✅ 에러 캐치됨:`, e?.message);
        console.log(`${TEST_PREFIX} status:`, e?.response?.status);
      }
    },

    /** 테스트 2: 연속 500 에러 → /error 페이지로 이동해야 함 */
    async multiple500() {
      __resetServerErrorCount(); // 깨끗한 상태에서 threshold 테스트
      console.log(`${TEST_PREFIX} 연속 500 에러 테스트 시작 (5개 동시 요청)`);
      console.log(`${TEST_PREFIX} 예상: threshold 도달 → /error 페이지로 이동`);

      const worker = getWorker();
      // GET 재시도 포함 최대 5 × 3 = 15
      const handler = createCountedAll500Handler(15);
      worker.use(handler);

      const requests = Array.from({ length: 5 }, (_, i) =>
        instance
          .get(`${REAL_API}?seq=${i}`)
          .catch((e: any) =>
            console.log(`${TEST_PREFIX} 요청 ${i + 1} 실패:`, e?.message)
          )
      );
      await Promise.allSettled(requests);
      console.log(`${TEST_PREFIX} ✅ /error 페이지로 이동했는지 확인`);
    },

    /** 테스트 3: GET 재시도 확인 → Network 탭에서 3번 요청 확인! */
    async getRetry() {
      __resetServerErrorCount();
      console.log(`${TEST_PREFIX} GET 재시도 테스트 시작`);
      console.log(
        `${TEST_PREFIX} 예상: 원본 1회 + 재시도 2회 = 총 3번 실패`
      );
      console.log(
        `${TEST_PREFIX} ⭐ Network 탭에서 ${REAL_API} 요청이 3번 나오는지 확인!`
      );

      const worker = getWorker();
      const handler = createCountedAll500Handler(3);
      worker.use(handler);

      try {
        await instance.get(REAL_API);
      } catch {
        console.log(`${TEST_PREFIX} ✅ 최종 실패 (재시도 2회 소진 후)`);
      }
    },

    /** 테스트 4: POST는 재시도하지 않는지 → 즉시 1번만 실패 */
    async postNoRetry() {
      __resetServerErrorCount();
      console.log(`${TEST_PREFIX} POST 재시도 금지 테스트 시작`);
      console.log(`${TEST_PREFIX} 예상: 즉시 1번만 실패 (재시도 없음)`);
      console.log(
        `${TEST_PREFIX} ⭐ Network 탭에서 POST 요청이 1번만 나오는지 확인!`
      );

      const worker = getWorker();
      const handler = createCountedAll500Handler(1);
      worker.use(handler);

      try {
        await instance.post(REAL_API, {});
      } catch {
        console.log(`${TEST_PREFIX} ✅ POST 즉시 실패 (재시도 없음)`);
      }
    },

    /** 수동 모드: 이후 모든 요청을 500으로 변환 */
    enable500() {
      if (is500Enabled) {
        console.log(
          `${TEST_PREFIX} 이미 활성화. disable500() 먼저 실행하세요.`
        );
        return;
      }

      const worker = getWorker();
      worker.use(createAll500Handler());
      is500Enabled = true;

      console.log(`${TEST_PREFIX} 🔴 500 모드 ON — 모든 API가 500 반환`);
      console.log(`${TEST_PREFIX} 해제: __errorTest.disable500()`);
    },

    /** 수동 모드 해제 */
    disable500() {
      if (!is500Enabled) {
        console.log(`${TEST_PREFIX} 활성화된 500 모드 없음`);
        return;
      }

      const worker = getWorker();
      worker.resetHandlers();
      is500Enabled = false;

      console.log(`${TEST_PREFIX} 🟢 500 모드 OFF — 정상 복귀`);
    },
  };

  (window as any).__errorTest = errorTest;
  console.log(
    `${TEST_PREFIX} 테스트 도구 등록됨. 콘솔에서 __errorTest.single500() 등 사용 가능`
  );
};
